/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { I18N } from 'aurelia-i18n';
import { ITasksConfig, IContactLogConfig } from 'app/core/models/essette-interfaces';
import { ISmartTaskBinding, ITaskContainer } from 'app/core/components/tasks/task';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { MemberService } from 'app/cd/services/member-service';
import { ContactLog } from 'app/cd/components/contact-log/contact-log';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SmartContact extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected targetId: string | null = null;

    protected task!: Core.Task;
    protected tasksConfig: ITasksConfig = {};
    protected member: CD.Member | null = null;
    protected memberId: string | null = null;

    protected validateForm = validate;
    protected methods: ReadonlyArray<CM.CaseContactLogMethod | UM.AuthContactLogMethod> = [];
    protected selectedMethod: CM.CaseContactLogMethod | UM.AuthContactLogMethod | null = null;
    protected methodReadonly: boolean = true;
    protected contacts: ReadonlyArray<CD.MemberContactComposite> = [];

    private container!: ITaskContainer;
    protected readonly: boolean = false;
    protected readonlyContactLogGrid: boolean = true;

    protected collapseNonContactLog: boolean = true;
    protected contactLogConfig: IContactLogConfig = {};
    protected contactLogTaskMethodId: string | null = null;

    ////****************** Element References ***************************************////
    protected $saveContactLogForm!: IForm;
    protected $contactLog!: ContactLog;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly contactLogService: ContactLogService,
        private readonly memberService: MemberService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected async activate(params: ISmartTaskBinding): Promise<void> {

        if(!params || !params.container || !params.targetId || !params.task || !params.task.id || !params.task.definition || !params.tasksConfig || !params.tasksConfig.smartTasksConfig || !params.tasksConfig.smartTasksConfig.contactLogConfig) {
            throw TypeError(`${SmartContact.name} Component Model not Bound.`);
        }

        this.targetId = params.targetId;
        this.tasksConfig = params.tasksConfig;
        this.memberId = params.relatedModels && params.relatedModels['CD.Member'] || null;

        this.task = params.task;
        if(params.task.definition.type && params.task.definition.type.argument && params.task.definition.type.argument.id) {
            this.contactLogTaskMethodId = params.task.definition.type.argument.id;
        }
        this.container = params.container;
        this.readonly = params.readonly || (this.task.isCompleted || false);
        this.contactLogConfig = {
            ...params.tasksConfig.smartTasksConfig.contactLogConfig
        }

        this.contactLogService.configure(this.contactLogConfig);

    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async addContactSuccess(): Promise<void> {
        if(this.container) {
            await this.container.completeTask(true)

            this.task.isCompleted = true;
        }
    }

    ////****************** Functions ************************************************////
    protected async handleSaveContactLogFormSuccess({ detail }: any): Promise<void> {
        const outcome: CM.CaseContactLogOutcome | UM.AuthContactLogOutcome | null = detail.form.selectedOutcome;

        if(outcome?.flag?.isSuccessful) {
            this.addContactSuccess()
        }

        if(this.$saveContactLogForm.reset) {
            this.$saveContactLogForm.reset();
        }
        this.$contactLog.reloadContactLog()
    }

    protected async reload(): Promise<void> {
        if(this.memberId) {
            await this.loadMember(this.memberId);
            await this.loadLookups();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMember(memberId: string): Promise<void> {
        const includes: string[] = [];
        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMember(memberId, includes);
            this.member = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.member?.id) return;

        try {
            this.state.beginLoad();

            const methodIncludes = [
                'outcomes'
            ];

            const contactIncludes: string[] = [
                'memberContact',
                'memberContact.relatedMember',
                'memberContact.relationship',
                'memberProvider',
                'memberProvider.relationship',
            ];

            const [methods, contacts] = await Promise.all(
                [
                    this.contactLogService.loadContactLogMethods({}, methodIncludes),
                    this.memberService.loadMemberContactsComposite(this.member.id, {}, contactIncludes)
                ]);

            // Insert the member themselves as the first item in the contacts options
            const selfContact = {
                memberContact: {
                    id: this.member.id,
                    relatedMember: {
                        id: this.member.id
                    }
                },
                $display: this.member.name?.full + ` (${this.i18n.tr('cd:member.$')})`
            }

            this.contacts = [selfContact, ...contacts.results];
            this.methods = methods.results;
            const method = this.methods.find(method => method.id === this.contactLogTaskMethodId)
            this.selectedMethod = method ? method : null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
